// web/src/legal/LegalPage.tsx
//
// Shared layout for /legal/{terms,privacy,refund}. Renders title,
// last-updated date, and a body that's either pre-rendered HTML
// (privacy, terms — exported from a legal-doc generator) or markdown
// (refund). Each route wrapper imports its content via Vite ?raw.
// Header + footer come from MarketingLayout.
//
// HTML bodies are author-trusted (bundled at build time, not user input),
// so dangerouslySetInnerHTML is acceptable here. The CSP still blocks any
// script execution and external resource loads.
import ReactMarkdown from "react-markdown";
import { Link } from "react-router-dom";

type Props = {
  title: string;
  lastUpdated: string;
} & (
  | { markdown: string; html?: never }
  | { html: string; markdown?: never }
);

export function LegalPage(props: Props) {
  const { title, lastUpdated } = props;
  return (
    <main className="container mx-auto max-w-3xl px-4 py-12">
      <header className="mb-8 space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">{title}</h1>
        <p className="text-sm text-muted-foreground">Last updated: {lastUpdated}</p>
      </header>
      {/* White reading surface so prose's light-mode defaults and the
          embedded HTML's hard-coded colors render with proper contrast,
          regardless of the surrounding (dark) theme. */}
      <article
        className="prose prose-neutral max-w-none rounded-lg bg-white px-6 py-8 text-neutral-900 shadow-sm sm:px-10 sm:py-10"
      >
        {"html" in props && props.html ? (
          <div dangerouslySetInnerHTML={{ __html: props.html }} />
        ) : (
          <ReactMarkdown
            components={{
              a: ({ href, children, ...rest }) => {
                if (href && href.startsWith("/")) {
                  return <Link to={href}>{children}</Link>;
                }
                return (
                  <a href={href} {...rest}>
                    {children}
                  </a>
                );
              },
            }}
          >
            {props.markdown!}
          </ReactMarkdown>
        )}
      </article>
    </main>
  );
}
